import { isServer } from "@tanstack/react-query";

import type {
  CatalogMetaRequest,
  CatalogMetaResponse,
  CatalogProductsRequest,
  CatalogProductsResponse,
} from "../types/catalog.type";
import type { ActiveCatalogFilters, FilterItem } from "../types/filters.type";

const FIVE_MINUTES = 1000 * 60 * 5; // 5 minutes
const REQUEST_TIMEOUT = 10000; // 10 seconds

function createTimeoutSignal(ms: number): AbortSignal {
  // Fallback for browsers that don't support AbortSignal.timeout (e.g. Safari < 16)
  if (
    typeof AbortSignal !== "undefined" &&
    typeof AbortSignal.timeout === "function"
  ) {
    return AbortSignal.timeout(ms);
  }

  const controller = new AbortController();
  setTimeout(
    () =>
      controller.abort(new DOMException("Signal timed out.", "TimeoutError")),
    ms,
  );
  return controller.signal;
}

function normalizePath(path: string) {
  return path
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

export function toFilterItems(filters: ActiveCatalogFilters): FilterItem[] {
  const items: FilterItem[] = [];
  let priceMin: string | undefined;
  let priceMax: string | undefined;

  for (const [key, value] of Object.entries(filters)) {
    if (key === "price_min") {
      if (typeof value === "number" && Number.isFinite(value)) {
        priceMin = String(value);
      }
      continue;
    }
    if (key === "price_max") {
      if (typeof value === "number" && Number.isFinite(value)) {
        priceMax = String(value);
      }
      continue;
    }
    if (typeof value === "boolean" && value) {
      items.push({ field: key, values: ["true"] });
      continue;
    }
    if (Array.isArray(value) && value.length > 0) {
      items.push({ field: key, values: value.map(String) });
    }
  }

  if (priceMin !== undefined || priceMax !== undefined) {
    items.push({ field: "price", values: [priceMin ?? "0", priceMax ?? "0"] });
  }

  return items;
}

export const getCatalogProductsQueryOptions = ({
  apiOrigin,
  path,
  sort,
  isCMSPath,
  pagination,
  enabled = true,
  extraHeaders,
  filters,
  locale,
}: {
  apiOrigin: string;
  path: string;
  isCMSPath?: boolean;
  sort?: string;
  pagination?: CatalogProductsRequest["pagination"];
  enabled?: boolean;
  extraHeaders?: Record<string, string>;
  filters?: ActiveCatalogFilters;
  locale: string;
}) => ({
  queryKey: [
    "catalog",
    normalizePath(path),
    pagination?.page,
    sort,
    filters,
    locale,
  ],
  queryFn: (): Promise<CatalogProductsResponse> =>
    fetchCatalogProducts({
      apiOrigin,
      path,
      extraHeaders,
      sort,
      pagination,
      isCMSPath,
      filters: filters ? toFilterItems(filters) : undefined,
    }),
  retry: 1,
  // staleTime > 0 também no server: o queryClient é per-request (cache()), então
  // permite que generateMetadata e CatalogBlock compartilhem o mesmo fetch de
  // products dentro do request (dedup), sem chamada de rede extra.
  staleTime: FIVE_MINUTES,
  enabled,
});

export const getCatalogInfiniteProductsQueryOptions = ({
  apiOrigin,
  path,
  isCMSPath,
  sort,
  enabled = true,
  extraHeaders,
  filters,
  locale,
}: {
  apiOrigin: string;
  path: string;
  isCMSPath?: boolean;
  sort?: string;
  enabled?: boolean;
  extraHeaders?: Record<string, string>;
  filters?: ActiveCatalogFilters;
  locale: string;
}) => ({
  queryKey: [
    "catalog-infinite",
    normalizePath(path),
    isCMSPath,
    sort,
    filters,
    locale,
  ],
  queryFn: ({
    pageParam,
  }: {
    pageParam: number;
  }): Promise<CatalogProductsResponse> =>
    fetchCatalogProducts({
      apiOrigin,
      path,
      extraHeaders,
      sort,
      pagination: { page: pageParam },
      isCMSPath,
      filters: filters ? toFilterItems(filters) : undefined,
    }),
  retry: 1,
  staleTime: isServer ? undefined : FIVE_MINUTES,
  enabled,
});

export const getCatalogMetaQueryOptions = ({
  apiOrigin,
  path,
  extraHeaders,
  filters,
  locale,
}: {
  apiOrigin: string;
  path: string;
  extraHeaders?: Record<string, string>;
  filters?: ActiveCatalogFilters;
  locale: string;
}) => {
  const filterItems = filters ? toFilterItems(filters) : [];
  const requestFilters = filterItems.length > 0 ? filterItems : undefined;

  return {
    queryKey: ["catalogMeta", normalizePath(path), requestFilters, locale],
    queryFn: async (): Promise<CatalogMetaResponse> => {
      const endpoint = `${apiOrigin}/api/v1/catalog/meta`;
      const body: CatalogMetaRequest = {
        path,
        ...(requestFilters ? { filters: requestFilters } : {}),
      };

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...extraHeaders,
        },
        body: JSON.stringify(body),
        signal: createTimeoutSignal(REQUEST_TIMEOUT),
      });

      return handleResponse<CatalogMetaResponse>(response);
    },
    retry: 1,
    staleTime: isServer ? undefined : FIVE_MINUTES,
  };
};

export async function fetchCatalogProducts({
  apiOrigin,
  path,
  filters,
  extraHeaders,
  sort,
  pagination,
  isCMSPath,
}: {
  apiOrigin: string;
  path: string;
  filters?: FilterItem[];
  extraHeaders?: Record<string, string>;
  sort?: string;
  pagination?: CatalogProductsRequest["pagination"];
  isCMSPath?: boolean;
}): Promise<CatalogProductsResponse> {
  const endpoint = `${apiOrigin}/api/v1/catalog/products`;

  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...extraHeaders,
    },
    body: JSON.stringify({
      path,
      ...(sort ? { sort } : {}),
      ...(pagination ? { pagination } : {}),
      ...(isCMSPath !== undefined ? { is_cms_path: isCMSPath } : {}),
      ...(filters && filters.length > 0 ? { filters } : {}),
    } as CatalogProductsRequest),
    signal: createTimeoutSignal(REQUEST_TIMEOUT),
  });

  return handleResponse<CatalogProductsResponse>(response);
}

async function handleResponse<T>(response: Response): Promise<T> {
  switch (response.status) {
    case 200:
      return response.json() as Promise<T>;
    case 400:
      throw new Error("Bad request");
    case 404:
      throw new Error("Catalog not found");
    case 500:
      throw new Error("Internal server error");
    default:
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      throw new Error("Unexpected response status");
  }
}
